import ALL_CHARACTERS from './allCharacters';
import { AddressSuggestion, BaseAddress, SimpleAddress } from './checkoutState';
import { config } from './config';
import { Country } from './countryData';

export const HOUSE_NUMBER_PART = `\\d+[${ALL_CHARACTERS}]*( *[-/,]? *\\d+[${ALL_CHARACTERS}]*)*?`;
const STREET_PART = `[${ALL_CHARACTERS}\\d\\-. '\\u2018-\\u2019]*?[${ALL_CHARACTERS}.]`;

const STREET_HOUSE_NUMBER_FROMAT_WITH_HOUSE_NUMBER_SECOND = new RegExp(
  `^(?<street>${STREET_PART}),? +(?<houseNumber>${HOUSE_NUMBER_PART})$`,
  'i'
);
const STREET_HOUSE_NUMBER_FROMAT_WITH_HOUSE_NUMBER_FIRST = new RegExp(
  `^(?<houseNumber>${HOUSE_NUMBER_PART}),? +(?<street>${STREET_PART})$`,
  'i'
);

export const getStreetHouseNumberFormat = (country: Country) =>
  config.addressForms[country].streetHouseNumber.houseNumber.position === 'second'
    ? STREET_HOUSE_NUMBER_FROMAT_WITH_HOUSE_NUMBER_SECOND
    : STREET_HOUSE_NUMBER_FROMAT_WITH_HOUSE_NUMBER_FIRST;

export const splitStreetHouseNumber = (streetHouseNumberInput: string, country: Country) => {
  const streetHouseNumberParts = getStreetHouseNumberFormat(country).exec(streetHouseNumberInput)?.groups;
  return { street: streetHouseNumberParts?.street ?? '', houseNumber: streetHouseNumberParts?.houseNumber ?? '' };
};

export const joinStreetHouseNumber = ({
  country,
  street,
  houseNumber,
}: Pick<BaseAddress, 'country' | 'street' | 'houseNumber'>) => {
  if (!houseNumber) {
    return street;
  }
  return config.addressForms[country.iso2Code].streetHouseNumber.houseNumber.position === 'second'
    ? `${street} ${houseNumber}`
    : `${houseNumber} ${street}`;
};

export const joinZipCodeCity = ({ country, zipCode, city }: Pick<BaseAddress, 'country' | 'zipCode' | 'city'>) => {
  if (!zipCode) {
    return city;
  }
  return config.addressForms[country.iso2Code].zipCodeCity.zipCode.position === 'first'
    ? `${zipCode} ${city}`
    : `${city} ${zipCode}`;
};

export const joinNames = ({ firstName, lastName }: Pick<BaseAddress, 'firstName' | 'lastName'>) =>
  config.names.lastName.position === 'second' ? `${firstName} ${lastName}` : `${lastName} ${firstName}`;

export const getAddressForSubmission = (addressValues: any & { streetHouseNumber: string }) => {
  if (config.addressForms[addressValues.country].streetHouseNumber.separateFields) {
    return {
      country: { iso2Code: addressValues.country },
      gender: addressValues.gender,
      firstName: addressValues.firstName,
      lastName: addressValues.lastName,
      street: addressValues.street.trim(),
      houseNumber: addressValues.houseNumber.trim(),
      additional: addressValues.additional || undefined,
      zipCode: addressValues.zipCode || undefined,
      city: addressValues.city,
      state: addressValues.state || undefined,
      signature: addressValues.signature,
    };
  } else {
    const { street, houseNumber } = splitStreetHouseNumber(addressValues.streetHouseNumber, addressValues.country);
    return {
      country: { iso2Code: addressValues.country },
      gender: addressValues.gender,
      firstName: addressValues.firstName,
      lastName: addressValues.lastName,
      street,
      houseNumber,
      additional: addressValues.additional || undefined,
      zipCode: addressValues.zipCode || undefined,
      city: addressValues.city,
      state: addressValues.state || undefined,
      signature: addressValues.signature,
    };
  }
};

export const getInitialAddressValues = (address: BaseAddress & Partial<AddressSuggestion>) => ({
  country: address.country.iso2Code,
  gender: address.salutationCode,
  title: address.title ?? '',
  firstName: address.firstName,
  lastName: address.lastName,
  street: address.street,
  houseNumber: address.houseNumber,
  streetHouseNumber: joinStreetHouseNumber(address),
  additional: address.additional ?? '',
  zipCode: address.zipCode ?? '',
  city: address.city,
  state: address.state ?? '',
  signature: address.signature,
});
